import { AuthUser } from '@/types/users'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import queryKeys from '../query-keys'
import { getDetail, getList, updateDetail } from '../request-wrappers'

export function useGetAuthUser({
  id,
  ...options
}: UseGetDetailOptions<AuthUser>) {
  const query = useGetDetail<AuthUser>({
    id,
    queryKey: queryKeys.authUsers.detail({ id, filters: options.filters }),
    serviceFn: (serviceFnOptions) =>
      getDetail<AuthUser>({ url: `/v2/auth/users/${id}`, ...serviceFnOptions }),
    ...options,
  })

  return {
    user: query.data,
    ...query,
  }
}

export function useGetAuthUserList(options: UseGetListOptions<AuthUser> = {}) {
  const query = useGetList<AuthUser>({
    queryKey: queryKeys.authUsers.list({ filters: options.filters }),
    serviceFn: (serviceFnOptions) =>
      getList<AuthUser>({ url: '/v2/auth/users', ...serviceFnOptions }),
    ...options,
  })

  return {
    users: query.allData || [],
    ...query,
  }
}

export type Role = {
  id: string
  description: string
  order: number
  is_assignable: boolean
}

/**
 * MARK: Custom Hook: Get Auth Roles
 * This hook fetches the list of roles from the auth service.
 */
export function useGetAuthRoles(options: UseGetListOptions<Role> = {}) {
  const query = useGetList<Role>({
    queryKey: ['auth-roles', options.filters],
    serviceFn: (serviceFnOptions) =>
      getList<Role>({ url: '/v2/auth/roles', ...serviceFnOptions }),
    ...options,
  })

  return {
    roles: query.allData || [],
    ...query,
  }
}

type UpdateUser = {
  id: string // can be stytch member_id or external_id/pd_user_id
  roles_ids?: string[]
  employee_ext_id?: string
}

/**
 * MARK: Action Hook: Update Auth User
 * This hook provides a mutation function to update the roles assigned to a user or their employee external ID.
 */
export function useUpdateAuthUser(
  options: Omit<UseUpdateDetailOptions<UpdateUser>, 'filters'> = {},
) {
  const mutation = useUpdateDetail<UpdateUser>({
    serviceFn: (serviceFnOptions) =>
      updateDetail<UpdateUser>({
        url: `/v2/auth/users/${serviceFnOptions.item.id}`,
        ...serviceFnOptions,
      }),
    ...options,
  })

  return {
    updateAuthUser: mutation.mutate,
    updateAuthUserAsync: mutation.mutateAsync,
    ...mutation,
  }
}
